import { Edit } from '@mui/icons-material'
import { Button, Card, CardContent, Grid, Typography, Divider, CircularProgress } from '@mui/material'
import { ExplanationAccordion, GreenText, LabelField } from 'components'
import { useEffect, useState } from 'react'
import TransportInformationAddEditDialog from './TransportInformationAddEditDialog'
import { CompanyDto, CompanyTransporterInformationDto } from 'dtos'
import * as controllers from 'controllers'
import { enqueueSnackbar } from 'notistack'
import { useAuth } from 'context'

interface TransporterInformationCardProps {
  company: CompanyDto
}

export default function TransporterInformationListCard({ company }: TransporterInformationCardProps) {
  const { permissions } = useAuth()

  const [companyTransporterInformation, setCompanyTransporterInformation] = useState<CompanyTransporterInformationDto>(
    new CompanyTransporterInformationDto()
  )
  const [isGettingCompanyTransporterInformation, setIsGettingCompanyTransporterInformation] = useState<boolean>(true)
  const [isSavingCompanyTransporterInformation, setIsSavingCompanyTransporterInformation] = useState<boolean>(false)
  const [transporterInformationAddEditDialogOpen, setTransporterInformationAddEditDialogOpen] = useState<boolean>(false)

  useEffect(() => {
    if (company.companyGuid) {
      setIsGettingCompanyTransporterInformation(true)
      controllers
        .getCompanyTransporterInformation(company.companyGuid)
        .then(response => {
          console.log(response.value)
          setCompanyTransporterInformation(response.value)
        })
        .finally(() => {
          setIsGettingCompanyTransporterInformation(false)
        })
    }
  }, [company])

  return (
    <>
      {permissions.COMPANIES_AND_PEOPLE_ADD_EDIT && (
        <TransportInformationAddEditDialog
          initialValues={companyTransporterInformation}
          loading={isSavingCompanyTransporterInformation}
          onClose={() => {
            setTransporterInformationAddEditDialogOpen(false)
          }}
          onSave={values => {
            setIsSavingCompanyTransporterInformation(true)
            controllers
              .updateCompanyTransporterInformation(company.companyGuid!, values)
              .then(response => {
                enqueueSnackbar('Transporter Information Saved Successfully!', { variant: 'success' })
                setCompanyTransporterInformation({
                  ...response.value,
                  transporterSCAC: response.value.transporterSCAC
                })
                setTransporterInformationAddEditDialogOpen(false)
              })
              .finally(() => {
                setIsSavingCompanyTransporterInformation(false)
              })
          }}
          open={transporterInformationAddEditDialogOpen}
        />
      )}

      <Card>
        <CardContent>
          <Grid container spacing={1} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm='auto'>
                <Typography fontWeight='bold' variant='h5'>
                  Transporter Information
                </Typography>
              </Grid>

              <Grid item xs={12} sm='auto'>
                {permissions.COMPANIES_AND_PEOPLE_ADD_EDIT && (
                  <Button
                    endIcon={<Edit />}
                    onClick={() => {
                      setTransporterInformationAddEditDialogOpen(true)
                    }}
                    variant='text'
                  >
                    EDIT
                  </Button>
                )}
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {isGettingCompanyTransporterInformation && (
              <Grid
                item
                container
                alignItems='center'
                justifyContent='center'
                sx={{
                  my: 1
                }}
              >
                <Grid>
                  <CircularProgress color='primary' size={50} />
                </Grid>
              </Grid>
            )}

            {!isGettingCompanyTransporterInformation && (
              <>
                <Grid item xs={12}>
                  <LabelField label='SCAC' value={companyTransporterInformation.transporterSCAC} />
                </Grid>

                <Grid item xs={12}>
                  <LabelField label='DOT #' value={companyTransporterInformation.dotNumber} />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <GreenText variant='outlined'>
                {`**Transporter Information Card:**
                
This card is displayed only if the company is marked as either a Inbound
Transporter Company and/or a Outbound Transporter Company in the system.

**Edit Button:**

Clicking this button will open the Transporter Information Edit Dialog.

**SCAC:**

Displays the SCAC entered in the Edit Transporter Information Dialog.

If no value has been entered for the SCAC, a “-” will be displayed.

**DOT #:**

Displays the DOT Number entered in the Edit Transporter Information Dialog.

If no value has been entered for the DOT Number, a “-” will be displayed.               
                
`}
              </GreenText>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
